import { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import TextField from '@material-ui/core/TextField';
import { Trans, t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import useEffectSkipFirst from '../../../utilities/UseEffectSkipFirst';
import useStyles from './ReportDialogStyles';
import { isEmailValid } from '../../../utilities/FormUtilities';
import { splitEmailString } from '../../../utilities/StringUtilities';

export const emailOptionsType = shape({
    recipients: arrayOf(string),
    subject: string,
    body: string,
});

export const defaultEmailOptions = {};

export default function EmailOptions({ options, autoFocus, isNew, onChange, onValidate }) {
    const classes = useStyles();
    const recipientsInputRef = useRef(null);
    const initialRecipients = options.recipients || [];
    const [recipients, setRecipients] = useState(initialRecipients);
    const [recipientsText, setRecipientsText] = useState(initialRecipients.join(', '));
    const [recipientsValid, setRecipientsValid] = useState(false);
    const [recipientsErrorMessage, setRecipientsErrorMessage] = useState(undefined);
    const [subject, setSubject] = useState(options.subject || '');
    const [body, setBody] = useState(options.body || '');

    useEffect(() => {
        onChange({
            recipients,
            subject,
            body,
        });
        // We don't need the onChange dependency and adding it can cause excessive updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipients, subject, body]);

    // Skip the first change, since it is always an empty string.  This ensures that the
    // field is not marked invalid until it is changed or blurred.
    useEffectSkipFirst(
        () => {
            const newRecipients = parseRecipients(recipientsText);
            validateRecipients(newRecipients);
        },
        [recipientsText],
        isNew
    );

    useEffect(() => {
        onValidate(recipientsValid);
        // We don't need the onChange dependency and adding it can cause excessive updates.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipientsValid]);

    function parseRecipients(value) {
        return splitEmailString(value);
    }

    function validateRecipients(values) {
        const hasValue = values.length > 0;
        const invalidRecipients = values.filter((recipient) => !isEmailValid(recipient));
        const recipientsAreValid = invalidRecipients.length === 0;
        const valid = hasValue && recipientsAreValid;
        setRecipientsValid(valid);

        if (!hasValue) {
            setRecipientsErrorMessage(i18n._(t`At least one email address is required`));
        } else if (!recipientsAreValid) {
            setRecipientsErrorMessage(i18n._(t`Please enter one ore more valid email addresses (separated by commas)`));
        } else {
            setRecipientsErrorMessage(undefined);
        }

        return valid;
    }

    function commitRecipients(value) {
        const newRecipients = parseRecipients(value);
        if (validateRecipients(newRecipients)) {
            setRecipients(newRecipients);
        }
    }

    function handleRecipientsChange(event) {
        const { value } = event.target;
        setRecipientsText(value);

        // Keep blur-based commits for normal typing, but handle browser autofill that can
        // update this field while focus is on a different input (observed in Edge).
        if (recipientsInputRef.current && document.activeElement !== recipientsInputRef.current) {
            commitRecipients(value);
        }
    }

    function handleRecipientsBlur(event) {
        const { value } = event.target;
        commitRecipients(value);
    }

    return (
        <div className={classes.emailOptions}>
            <TextField
                id="report-email-recipients"
                label={<Trans>Send To</Trans>}
                value={recipientsText}
                required
                fullWidth
                error={!!recipientsErrorMessage}
                helperText={recipientsErrorMessage}
                inputRef={recipientsInputRef}
                InputProps={{ autoFocus, inputProps: { onBlur: handleRecipientsBlur } }}
                onChange={handleRecipientsChange}
            />
            <TextField
                id="report-email-subject"
                label={<Trans>Email Subject</Trans>}
                value={subject}
                fullWidth
                onChange={(event) => setSubject(event.target.value)}
            />
            <TextField
                id="report-email-body"
                label={<Trans>Email Body</Trans>}
                value={body}
                fullWidth
                multiline
                rowsMax={4}
                onChange={(event) => setBody(event.target.value)}
            />
        </div>
    );
}

EmailOptions.propTypes = {
    options: emailOptionsType.isRequired,
    autoFocus: bool,
    isNew: bool,
    onChange: func.isRequired,
    onValidate: func.isRequired,
};

EmailOptions.defaultProps = {
    autoFocus: false,
    isNew: true,
};
